import React, { ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AddCircle } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import { ListingForm } from 'components/shared/TableListing';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { DocumentField } from './DocumentField';
import { ChecklistField } from './ChecklistField';
import { AttributeCellFields } from './AttributeCellField';
import { AttributeFieldProps, StagesFormValues } from '../../../../../types';

interface Props {
  isEdit: boolean;
  rowIndex: number;
  disabled?: boolean;
  prefix: keyof Pick<StagesFormValues, 'listOfAttributes'>;
}

export const AttributeCell = ({ isEdit, rowIndex, prefix, disabled = false }: Props) => {
  const { control } = useFormContext<ListingForm<StagesFormValues>>();
  const { fields, remove, append } = useFieldArray({ control, name: `bodyFields.${rowIndex}.${prefix}` });

  const AttributeNodeByType: Record<
    StagesFormValues['listOfAttributes'][number]['field'],
    (deps: AttributeFieldProps) => ReactNode
  > = {
    checklist: deps => <ChecklistField {...deps} />,
    document: deps => <DocumentField {...deps} />,
  };

  return (
    <Stack gap="0.5rem" width="100%" height="100%">
      <Stack gap="0.7rem">
        <RenderWithCondition
          condition={!!fields.length}
          fallBack={<Typography color="textDisabled">List empty</Typography>}
        >
          {fields.map((field, attributeIndex) => (
            <AttributeCellFields
              key={field.id}
              isEdit={isEdit}
              disabled={disabled || isEdit}
              node={AttributeNodeByType[field.field]({ rowIndex, disabled, attributeIndex, prefix })}
              onRemove={() => {
                remove(attributeIndex);
              }}
            />
          ))}
        </RenderWithCondition>
      </Stack>

      {!isEdit && (
        <Stack direction="row" gap="0.5rem">
          <Button
            size="small"
            disabled={disabled}
            startIcon={<AddCircle fontSize="small" />}
            onClick={() =>
              append({
                field: 'checklist',
                name: '',
                value: false,
                isRequired: false,
                id: uuidv4(),
              })
            }
          >
            Add Checklist
          </Button>

          <Button
            size="small"
            disabled={disabled}
            startIcon={<AddCircle fontSize="small" />}
            onClick={() =>
              append({
                field: 'document',
                name: '',
                notify: [],
                value: false,
                isPII: false,
                id: uuidv4(),
                isRequired: false,
                availableForRoles: [],
              })
            }
          >
            Add Document
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
